import axios from 'axios';

/**
 * Generates the HTML content for the invitation email.
 * @param {string} inviteLink - The full URL for the invitation.
 * @returns {string} - The HTML string.
 */
const getInviteHtml = (inviteLink) => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Welcome to SpaceHR</title>
</head>
<body style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f4f7f6; color: #333333;">
  <div style="max-width: 600px; margin: 40px auto; background: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);">
    <div style="background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%); padding: 40px 0; text-align: center;">
      <div style="font-size: 28px; font-weight: 800; color: #ffffff; text-decoration: none; letter-spacing: -0.5px;">SpaceHR</div>
    </div>
    <div style="padding: 48px 40px; text-align: center;">
      <h1 style="font-size: 24px; font-weight: 700; color: #111827; margin: 0 0 16px;">You've been invited to join SpaceHR</h1>
      <p style="font-size: 16px; line-height: 1.6; color: #4b5563; margin: 0 0 32px;">
        We're excited to have you on board! You have been invited to join your team on the SpaceHR platform. 
        Click the button below to set up your account and get started.
      </p>
      <a href="${inviteLink}" style="display: inline-block; background-color: #0d9488; color: #ffffff; font-size: 16px; font-weight: 600; text-decoration: none; padding: 16px 32px; border-radius: 8px; transition: background-color 0.2s ease; box-shadow: 0 2px 4px rgba(13, 148, 136, 0.2);">Accept Invitation</a>
      <p style="margin-top: 32px; font-size: 14px; color: #6b7280;">
        Or copy and paste this link into your browser:<br>
        <a href="${inviteLink}" style="color: #0d9488; text-decoration: none;">${inviteLink}</a>
      </p>
    </div>
    <div style="padding: 32px; background-color: #f9fafb; text-align: center; border-top: 1px solid #e5e7eb;">
      <p style="font-size: 14px; color: #9ca3af; margin: 0;">
        © 2026 SpaceHR. All rights reserved.
      </p>
    </div>
  </div>
</body>
</html>
  `;
};

/**
 * Sends batch invitation emails using Resend.
 * @param {string[]} recipients - Array of email addresses.
 * @param {string} inviteLink - The invite URL.
 * @returns {Promise<any>} - The API response.
 */
/**
 * Sends invitation emails using Resend (Single Email Endpoint).
 * Validates calls loop through recipients since we are using the single endpoint.
 * @param {string[]} recipients - Array of email addresses.
 * @param {string} inviteLink - The invite URL.
 * @returns {Promise<any>} - The API response.
 */
export const sendBatchInvites = async (recipients, inviteLink) => {
  // Use worker URL from env if available, otherwise fallback to local proxy
  // Use worker URL from env if available, otherwise fallback to local proxy
  const workerUrl = process.env.REACT_APP_EMAIL_WORKER_URL;
  const backendUrl = workerUrl ? `${workerUrl}/send-email` : '/api/send-email';

  const results = [];
  const errors = [];

  for (const email of recipients) {
    try {
      const response = await axios.post(
        backendUrl,
        {
          to: email.trim(),
          invite_link: inviteLink
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 'error') {
        throw new Error(JSON.stringify(response.data));
      }

      results.push(response.data);
    } catch (error) {
      console.error(`Failed to send to ${email}:`, error);
      errors.push({ email, error });
    }
  }

  if (errors.length > 0 && results.length === 0) {
    throw errors[0].error;
  }

  return { results, errors };
};
